// Inline customer-upload library. Shows uploads under the dropzone so the
// user sees their assets accumulating without opening the full LibraryDrawer.
//
// Persistence behaviour:
//  - SIGNED IN  → fetched from /api/library/uploads (kind='artwork' files
//    owned by the user). Survives across sessions. Supports user-created
//    folders + filename search via server-side filters.
//  - GUEST      → local-only list tracked in component state. Visible during
//    the current page session, lost on refresh. A clear sign-in nudge sits
//    at the bottom of the library. No folders/search (no persistence target).
//
// Either way, the editor calls `addUpload()` on the ref after each successful
// POST /api/files so the new asset shows up instantly.
import { useCallback, useEffect, useImperativeHandle, useState, forwardRef, useRef } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../lib/api'

export interface UploadAsset {
  id: string
  originalName: string
  url: string
  widthPx: number | null
  heightPx: number | null
  folderId?: string | null
  createdAt: string
}

interface Folder { id: string; name: string; sortOrder: number; fileCount: number }

export interface InlineUploadLibraryHandle {
  /** Re-fetch the server list. No-op for guests. */
  refresh: () => Promise<void>
  /** Add an asset locally (called by the editor after a successful upload). */
  addUpload: (asset: UploadAsset) => void
}

const PAGE_SIZE = 12

const InlineUploadLibrary = forwardRef<InlineUploadLibraryHandle, {
  onUse: (asset: UploadAsset) => void
  onOpenFullLibrary?: () => void
}>(function InlineUploadLibrary({ onUse, onOpenFullLibrary }, ref) {
  const { user, getAccessToken } = useAuth()
  const apiSrc = useApiSrc()

  // Local list of uploads from THIS session. Always populated by the editor
  // via addUpload(); for guests this is the only source. For signed-in users
  // it's merged with the server list (deduped by id).
  const [local, setLocal] = useState<UploadAsset[]>([])
  const [server, setServer] = useState<UploadAsset[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [rootCount, setRootCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showAll, setShowAll] = useState(false)
  // 'all' shows everything; 'root' shows uploads with no folder; otherwise a folder id.
  const [folderFilter, setFolderFilter] = useState<'all' | 'root' | string>('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [moveOpen, setMoveOpen] = useState<string | null>(null) // file id whose move-to popover is showing
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [folderDraft, setFolderDraft] = useState('')
  // Multi-select mode: when active, tiles show a checkbox overlay and clicks
  // toggle selection instead of using the asset on the canvas. Set of file ids.
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkBusy, setBulkBusy] = useState(false)

  // Debounce search input → server query. 200ms is below typing-feel
  // threshold but keeps refresh count sane on long inputs.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 200)
    return () => window.clearTimeout(t)
  }, [search])

  const fetchFolders = useCallback(async () => {
    if (!user) return
    try {
      const d = await apiFetch<{ items: Folder[]; rootCount: number }>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/library/folders',
      )
      setFolders(d.items); setRootCount(d.rootCount)
    } catch { /* anon / auth blip — keep what we have */ }
  }, [user, getAccessToken])

  const fetchUploads = useCallback(async () => {
    if (!user) return
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (folderFilter === 'root') params.set('folderId', 'root')
      else if (folderFilter !== 'all') params.set('folderId', folderFilter)
      if (debouncedSearch) params.set('q', debouncedSearch)
      const qs = params.toString()
      const d = await apiFetch<{ items: UploadAsset[] }>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/library/uploads' + (qs ? '?' + qs : ''),
      )
      setServer(d.items)
    } catch { /* anon / auth blip — keep what we have */ }
    finally { setLoading(false) }
  }, [user, getAccessToken, folderFilter, debouncedSearch])

  const refresh = useCallback(async () => {
    await Promise.all([fetchFolders(), fetchUploads()])
  }, [fetchFolders, fetchUploads])

  const addUpload = useCallback((asset: UploadAsset) => {
    setLocal((prev) => prev.find((a) => a.id === asset.id) ? prev : [asset, ...prev])
    // For signed-in users, also refresh from server so the canonical list
    // (with widthPx/heightPx and any other metadata) backfills the local one.
    if (user) void refresh()
  }, [user, refresh])

  useImperativeHandle(ref, () => ({ refresh, addUpload }), [refresh, addUpload])

  // Re-pull whenever filter/search changes; folders list only refreshes when
  // refresh() is explicitly called (after upload/delete/folder mutations).
  useEffect(() => { void fetchUploads() }, [fetchUploads])
  // First-load folder fetch.
  useEffect(() => { void fetchFolders() }, [fetchFolders])

  // Merged + deduped (newest first). Server wins on conflict because it has
  // the canonical metadata. When signed in with active filters the merged
  // list is filtered client-side too so the just-uploaded `local` item
  // doesn't pop in across folders.
  const items: UploadAsset[] = (() => {
    const map = new Map<string, UploadAsset>()
    local.forEach((a) => map.set(a.id, a))
    server.forEach((a) => map.set(a.id, a))
    let arr = Array.from(map.values())
    if (user) {
      if (folderFilter === 'root')      arr = arr.filter((a) => !a.folderId)
      else if (folderFilter !== 'all')  arr = arr.filter((a) => a.folderId === folderFilter)
      if (debouncedSearch)              arr = arr.filter((a) => a.originalName.toLowerCase().includes(debouncedSearch.toLowerCase()))
    }
    return arr.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  })()
  const visible = showAll ? items : items.slice(0, PAGE_SIZE)

  async function createFolder() {
    const name = folderDraft.trim()
    if (!name) { setCreatingFolder(false); return }
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/folders', {
        method: 'POST', body: JSON.stringify({ name }),
      })
      setFolderDraft(''); setCreatingFolder(false)
      await fetchFolders()
    } catch (e) { alert((e as Error).message) }
  }

  async function renameFolder(f: Folder) {
    const next = window.prompt('Rename folder', f.name)?.trim()
    if (!next || next === f.name) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/folders/${f.id}`, {
        method: 'PATCH', body: JSON.stringify({ name: next }),
      })
      await fetchFolders()
    } catch (e) { alert((e as Error).message) }
  }

  async function deleteFolder(f: Folder) {
    if (!confirm(`Delete folder "${f.name}"? Files inside will move back to the root view; they won't be deleted.`)) return
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/folders/${f.id}`, { method: 'DELETE' })
      if (folderFilter === f.id) setFolderFilter('all')
      await refresh()
    } catch (e) { alert((e as Error).message) }
  }

  async function moveTo(a: UploadAsset, folderId: string | null) {
    setMoveOpen(null)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/uploads/${a.id}`, {
        method: 'PATCH', body: JSON.stringify({ folderId }),
      })
      // Update local view immediately so the move feels instant even before refresh.
      setServer((prev) => prev.map((x) => x.id === a.id ? { ...x, folderId } : x))
      setLocal((prev) => prev.map((x) => x.id === a.id ? { ...x, folderId } : x))
      await refresh()
    } catch (e) { alert((e as Error).message) }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function exitSelectMode() {
    setSelectMode(false); setSelected(new Set())
  }
  async function deleteSelected() {
    if (selected.size === 0) return
    if (!confirm(`Delete ${selected.size} item${selected.size === 1 ? '' : 's'} permanently? This cannot be undone.`)) return
    const ids = Array.from(selected)
    setBulkBusy(true)
    // Optimistic local remove first so the UI feels instant.
    setLocal((prev) => prev.filter((x) => !selected.has(x.id)))
    setServer((prev) => prev.filter((x) => !selected.has(x.id)))
    try {
      if (user) {
        await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/library/uploads/bulk-delete', {
          method: 'POST', body: JSON.stringify({ ids }),
        })
      }
      await refresh()
    } catch (e) { alert((e as Error).message) }
    finally { setBulkBusy(false); exitSelectMode() }
  }

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="eyebrow !mb-0">Your library ({items.length})</div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <button onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
              className={'text-[11px] font-semibold uppercase tracking-wide px-2 py-1 rounded border transition-colors ' +
                (selectMode ? 'bg-gold text-ink border-gold-dark' : 'text-ink-2 border-border hover:border-gold hover:text-ink')}>
              {selectMode ? 'Done' : 'Select'}
            </button>
          )}
          {!selectMode && onOpenFullLibrary && items.length > 0 && user && (
            <button onClick={onOpenFullLibrary} className="text-[11px] text-gold-dark hover:underline">Open full library →</button>
          )}
        </div>
      </div>
      {/* Bulk-action bar — visible when in Select mode. Mirrors the cleaner
          layout from the LibraryDrawer: counter on the left, secondary
          links in the middle, primary destructive action on the right
          (gated until at least one item is selected). */}
      {selectMode && (
        <div className="flex items-center justify-between gap-2 mb-2 px-2 py-1.5 rounded border border-border bg-surface flex-wrap">
          <span className="text-[11px] text-ink-2"><strong className="text-ink">{selected.size}</strong> of {items.length} selected</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(new Set(items.map((a) => a.id)))} className="text-[11px] text-ink-3 hover:text-ink underline">Select all</button>
            <button onClick={() => setSelected(new Set())} className="text-[11px] text-ink-3 hover:text-ink underline">Clear</button>
            <button
              onClick={() => void deleteSelected()}
              disabled={selected.size === 0 || bulkBusy}
              className="text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {bulkBusy ? '…' : `Delete ${selected.size || ''} forever`}
            </button>
          </div>
        </div>
      )}
      {/* Folders + search bar — signed-in users only (guests get no persistence target). */}
      {user && (
        <div className="space-y-2 mb-3">
          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search artwork…"
              className="w-full text-[12px] px-2 py-1.5 pr-7 border border-border rounded bg-white focus:border-gold focus:outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} title="Clear search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink text-sm leading-none">×</button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <FolderChip label="All" count={folders.reduce((n, f) => n + f.fileCount, 0) + rootCount} active={folderFilter === 'all'} onClick={() => setFolderFilter('all')} />
            <FolderChip label="Unfiled" count={rootCount} active={folderFilter === 'root'} onClick={() => setFolderFilter('root')} />
            {folders.map((f) => (
              <FolderChip key={f.id} label={f.name} count={f.fileCount} active={folderFilter === f.id}
                onClick={() => setFolderFilter(f.id)}
                onRename={() => renameFolder(f)}
                onDelete={() => deleteFolder(f)} />
            ))}
            {!creatingFolder && (
              <button onClick={() => setCreatingFolder(true)}
                className="text-[11px] px-2 py-0.5 rounded border border-dashed border-border text-ink-3 hover:text-ink hover:border-gold">+ New folder</button>
            )}
            {creatingFolder && (
              <span className="inline-flex items-center gap-1 border border-gold rounded px-1">
                <input
                  autoFocus value={folderDraft}
                  onChange={(e) => setFolderDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void createFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setFolderDraft('') } }}
                  onBlur={() => void createFolder()}
                  placeholder="Folder name"
                  className="text-[11px] px-1 py-0.5 outline-none w-28" />
              </span>
            )}
          </div>
        </div>
      )}

      {loading && items.length === 0 && <div className="text-[11px] text-ink-3">Loading…</div>}
      {!loading && items.length === 0 && (
        <p className="text-[11px] text-ink-3 leading-relaxed">
          {debouncedSearch
            ? `No artwork matches "${debouncedSearch}".`
            : (user ? 'Uploaded artwork shows up here. Saved across sessions.' : 'Uploaded artwork shows up here. Available for this session only — sign in to keep your library.')}
        </p>
      )}

      {items.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {visible.map((a) => {
              const isSel = selected.has(a.id)
              return (
                <div key={a.id} className="relative group aspect-square">
                  <button
                    onClick={() => selectMode ? toggleSelect(a.id) : onUse(a)}
                    title={selectMode ? (isSel ? 'Deselect' : 'Select') + ' ' + a.originalName : `Use ${a.originalName}`}
                    className={'w-full h-full bg-surface border rounded overflow-hidden transition-colors flex items-center justify-center ' +
                      (selectMode && isSel ? 'border-gold-dark ring-2 ring-gold' : 'border-border hover:border-gold')}>
                    <img src={apiSrc(a.url)} alt={a.originalName} className="max-w-full max-h-full object-contain" />
                  </button>
                  {/* Select-mode checkbox overlay (always visible in select mode). */}
                  {selectMode && (
                    <span className={'absolute top-1 left-1 w-5 h-5 rounded flex items-center justify-center text-[11px] font-bold shadow-sm pointer-events-none ' +
                      (isSel ? 'bg-gold text-ink border border-gold-dark' : 'bg-white/95 text-ink-3 border border-border')}>
                      {isSel ? '✓' : ''}
                    </span>
                  )}
                  {/* Hover-only action chips: move (signed-in only) + delete.
                      Suppressed in select mode so the whole tile is one click. */}
                  {!selectMode && (
                    <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {user && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setMoveOpen(moveOpen === a.id ? null : a.id) }}
                          title="Move to folder"
                          className="w-5 h-5 rounded bg-white/95 border border-border text-ink-3 hover:text-gold-dark hover:border-gold flex items-center justify-center text-[10px] leading-none shadow-sm"
                        >📁</button>
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation()
                          if (!confirm(`Delete ${a.originalName} forever? This cannot be undone.`)) return
                          setLocal((prev) => prev.filter((x) => x.id !== a.id))
                          setServer((prev) => prev.filter((x) => x.id !== a.id))
                          if (user) {
                            try {
                              await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/library/uploads/${a.id}`, { method: 'DELETE' })
                              await fetchFolders()
                            } catch (err) { console.warn('remove failed:', (err as Error).message) }
                          }
                        }}
                        title="Delete forever"
                        className="w-5 h-5 rounded bg-white/95 border border-border text-ink-3 hover:text-red-600 hover:border-red-300 flex items-center justify-center text-xs leading-none shadow-sm"
                      >×</button>
                    </div>
                  )}
                  {/* Move-to popover */}
                  {moveOpen === a.id && user && !selectMode && (
                    <MovePopover
                      folders={folders}
                      currentFolderId={a.folderId ?? null}
                      onClose={() => setMoveOpen(null)}
                      onMove={(fid) => moveTo(a, fid)}
                    />
                  )}
                </div>
              )
            })}
          </div>
          {items.length > PAGE_SIZE && (
            <button onClick={() => setShowAll((s) => !s)} className="text-[11px] text-gold-dark hover:underline mt-2">
              {showAll ? 'Show fewer' : `Show all ${items.length}`}
            </button>
          )}
          {!user && (
            <p className="text-[11px] text-ink-3 leading-relaxed mt-3 border-t border-border pt-2">
              Session-only — these uploads will be gone after you close the tab. <span className="text-ink-2 font-semibold">Sign in</span> to save them across sessions.
            </p>
          )}
        </>
      )}
    </div>
  )
})

// Small folder chip; hover reveals rename/delete kebab on user folders only
// (the built-in "All" + "Unfiled" chips don't pass handlers, so the kebab
// stays hidden for them).
function FolderChip({ label, count, active, onClick, onRename, onDelete }: {
  label: string; count: number; active: boolean
  onClick: () => void
  onRename?: () => void; onDelete?: () => void
}) {
  const [openMenu, setOpenMenu] = useState(false)
  return (
    <span className="relative group">
      <button onClick={onClick}
        className={'text-[11px] px-2 py-0.5 rounded border ' + (active ? 'border-gold bg-gold/10 text-ink' : 'border-border text-ink-3 hover:text-ink hover:border-gold-dark/40')}>
        {label} <span className="text-ink-3">({count})</span>
      </button>
      {(onRename || onDelete) && (
        <button onClick={(e) => { e.stopPropagation(); setOpenMenu((o) => !o) }}
          title="Folder options"
          className="ml-0.5 text-ink-3 hover:text-ink text-[11px] opacity-0 group-hover:opacity-100">⋯</button>
      )}
      {openMenu && (
        <span className="absolute left-0 top-full mt-1 z-20 bg-white border border-border rounded shadow-md text-[11px] py-1 min-w-[110px]">
          {onRename && <button onClick={() => { setOpenMenu(false); onRename() }} className="block w-full text-left px-2 py-1 hover:bg-surface">Rename</button>}
          {onDelete && <button onClick={() => { setOpenMenu(false); onDelete() }} className="block w-full text-left px-2 py-1 text-red-600 hover:bg-red-50">Delete</button>}
        </span>
      )}
    </span>
  )
}

// Popover anchored to the upload tile that lets the user pick a destination
// folder. Click-outside closes it (handled by the outer overlay).
function MovePopover({ folders, currentFolderId, onClose, onMove }: {
  folders: Folder[]
  currentFolderId: string | null
  onClose: () => void
  onMove: (folderId: string | null) => void
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    function onDocClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) onClose() }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [onClose])
  return (
    <div ref={ref} className="absolute top-7 right-1 z-30 bg-white border border-border rounded shadow-md text-[11px] py-1 min-w-[140px] max-h-48 overflow-y-auto">
      <div className="px-2 py-1 text-ink-3 uppercase tracking-wider text-[10px]">Move to</div>
      <button onClick={() => onMove(null)}
        className={'block w-full text-left px-2 py-1 hover:bg-surface ' + (currentFolderId === null ? 'text-gold-dark font-semibold' : '')}>Unfiled</button>
      {folders.length === 0 && <div className="px-2 py-1 text-ink-3">No folders yet</div>}
      {folders.map((f) => (
        <button key={f.id} onClick={() => onMove(f.id)}
          className={'block w-full text-left px-2 py-1 hover:bg-surface ' + (currentFolderId === f.id ? 'text-gold-dark font-semibold' : '')}>{f.name}</button>
      ))}
    </div>
  )
}

export default InlineUploadLibrary
